// Auth and "my reports" (plan.md §4.4, §4.5).
//
// Signing in is optional and mostly cosmetic in v1, and the UI should say so
// rather than implying an account is needed: reporting and corroborating both
// work fully anonymously and always will.
//
// Also hosts the language switcher — the account/preferences surface is the
// least-cluttered place to expose a locale choice.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trans } from 'react-i18next';
import { iconFor } from '../lib/icons.js';
import { sendMagicLink, signOut } from '../lib/auth.js';
import { fetchMyReports } from '../lib/db.js';
import { getSessionToken } from '../lib/session.js';
import { relativeTime, timeUntil } from '../lib/format.js';
import { useLocale, changeLocale, SUPPORTED_LOCALES } from '../lib/i18n.js';
import { localizeCategoryName } from '../lib/taxonomy-i18n.js';

const LOCALE_LABELS = {
  'en-GB': 'account.languageEnGB',
  'en-US': 'account.languageEnUS',
};

export default function AccountSheet({ user, onClose, onOpenReport }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const [reports, setReports] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchMyReports(getSessionToken())
      .then((data) => { if (!cancelled) setReports(data); })
      .catch(() => { if (!cancelled) setReports([]); });
    return () => { cancelled = true; };
  }, [user]);

  async function submit(event) {
    event.preventDefault();
    setSending(true);
    setError(null);
    try {
      await sendMagicLink(email);
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="sheet sheet--tall" role="dialog" aria-modal="true" aria-label={t('account.aria')}>
      <header className="sheet__header">
        <h2 className="sheet__title">{t('account.title')}</h2>
        <button type="button" className="btn btn--icon" onClick={onClose} aria-label={t('nav.close')}>×</button>
      </header>

      <div className="sheet__body">
        {user ? (
          <div className="account">
            <p className="account__who">
              <Trans i18nKey="account.signedInAs" components={{ strong: <strong /> }} values={{ email: user.email }} />
            </p>
            <button type="button" className="btn btn--ghost" onClick={() => signOut()}>{t('account.signOut')}</button>
          </div>
        ) : sent ? (
          <div className="account">
            <p className="account__who">{t('account.checkEmail')}</p>
            <p className="hint">
              <Trans i18nKey="account.sentBody" components={{ strong: <strong /> }} values={{ email }} />
            </p>
          </div>
        ) : (
          <form className="account" onSubmit={submit}>
            <p className="hint">
              {t('account.noAccountNeeded')}
            </p>
            <label className="field">
              <span className="field__label">{t('account.emailLabel')}</span>
              <input
                type="email"
                className="field__input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('account.emailPlaceholder')}
                required
                autoComplete="email"
              />
            </label>
            {error && <p className="error" role="alert">{error}</p>}
            <button type="submit" className="btn btn--primary" disabled={sending || !email.trim()}>
              {sending ? t('account.sending') : t('account.sendLink')}
            </button>
            <p className="hint hint--small">
              {t('account.emailNote')}
            </p>
          </form>
        )}

        <div className="language-switcher">
          <label className="field">
            <span className="field__label">{t('account.languageLabel')}</span>
            <select
              className="field__input"
              value={locale}
              onChange={(e) => changeLocale(e.target.value)}
            >
              {SUPPORTED_LOCALES.map((l) => (
                <option key={l} value={l}>{t(LOCALE_LABELS[l])}</option>
              ))}
            </select>
          </label>
        </div>

        <h3 className="sheet__section-title">{t('account.myReportsTitle')}</h3>

        {reports === null && <p className="hint">{t('account.loading')}</p>}

        {reports?.length === 0 && (
          <div className="empty">
            <p className="empty__title">{t('account.emptyTitle')}</p>
            <p className="empty__body">
              {t('account.emptyBody')}
            </p>
          </div>
        )}

        {reports?.length > 0 && (
          <ul className="mine">
            {reports.map((report) => (
              <li key={report.id}>
                <button
                  type="button"
                  className={`mine__row${report.is_active ? '' : ' mine__row--inactive'}`}
                  onClick={() => onOpenReport(report.id)}
                >
                  <span className="mine__icon" aria-hidden="true">
                    {iconFor(report.category_icon, report.collection_slug)}
                  </span>
                  <span className="mine__text">
                    <span className="mine__name">
                      {localizeCategoryName(report.category_slug, locale) ?? report.category_name}
                    </span>
                    <span className="mine__meta">
                      {relativeTime(report.created_at)}
                      {report.corroboration_count > 0 && <> · {t('account.confirmed', { count: report.corroboration_count })}</>}
                      {report.is_active
                        ? <> · {t('account.disappearsIn', { time: timeUntil(report.expires_at) })}</>
                        : <> · {t('account.expired')}</>}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
